import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'

interface UsePollingResult<T> {
  data: T | null
  error: boolean
  lastUpdatedAt: Date | null
  loading: boolean
  refetch: () => Promise<void>
}

export function usePolling<T>(path: string, intervalMs: number): UsePollingResult<T> {
  const { context } = useContext(EbAuthenticatedAppContext)
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)
  const isMountedRef = useRef(true)

  const fetchOnce = useCallback(async () => {
    try {
      const response = await context.restApiClient.buildRequest(path).deserializeResponseToJson<T>().getAsync()
      if (!isMountedRef.current) return
      if (response.json) {
        setData(response.json)
        setError(false)
        setLastUpdatedAt(new Date())
      } else if (response.error) {
        setError(true)
      }
    } catch {
      if (isMountedRef.current) setError(true)
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }, [context, path])

  useEffect(() => {
    isMountedRef.current = true
    let timer: ReturnType<typeof setTimeout> | null = null

    const tick = () => {
      if (document.visibilityState === 'visible') {
        fetchOnce()
      }
      timer = setTimeout(tick, intervalMs)
    }

    fetchOnce()
    timer = setTimeout(tick, intervalMs)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchOnce()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      isMountedRef.current = false
      if (timer) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [fetchOnce, intervalMs])

  return { data, error, lastUpdatedAt, loading, refetch: fetchOnce }
}
